// SB6-ANA-POP-02 — Settings drawer "Multi-window layout" section.
//
// Lists each open pop-out window and the widgets inside it, with a per-widget
// "Bring back" (→ main dashboard) and a per-window "Close window" (→ all its
// widgets return to the main dashboard). Source of truth: the workspace store;
// changes sync to the actual windows over BroadcastChannel (they re-render or
// self-close accordingly).
import { Box, Button, Stack, Typography } from "@mui/material";
import {
  usePopoutWorkspaceStore,
  type PopoutWindowState,
} from "./popoutWorkspaceStore.js";
import { WIDGET_LABEL, type PopoutWidgetKey } from "./windowSizes.js";

export function MultiWindowLayoutSection(): React.ReactElement {
  const windows = usePopoutWorkspaceStore((s) => s.windows);
  const unassign = usePopoutWorkspaceStore((s) => s.unassign);
  const closeWindow = usePopoutWorkspaceStore((s) => s.closeWindow);

  if (windows.length === 0) {
    return (
      <Box
        role="region"
        aria-label="Multi-window layout"
        data-testid="multi-window-layout-empty"
        sx={{ py: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          No widgets popped out. Use the pop-out icon in any widget header to
          move it into its own window.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack
      spacing={1}
      role="region"
      aria-label="Multi-window layout"
      data-testid="multi-window-layout"
    >
      {windows.map((w: PopoutWindowState, idx) => (
        <Box
          key={w.windowId}
          data-testid={"multi-window-window-" + String(idx)}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Window {idx + 1}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                closeWindow(w.windowId);
              }}
              data-testid={"multi-window-close-" + String(idx)}
            >
              Close window
            </Button>
          </Stack>
          <Stack spacing={0.5}>
            {w.layout.map((cell) => {
              const widget = cell.i as PopoutWidgetKey;
              return (
                <Box
                  key={widget}
                  data-testid={"multi-window-row-" + widget}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pl: 0.5,
                  }}
                >
                  <Typography variant="body2">
                    {WIDGET_LABEL[widget]}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      unassign(widget);
                    }}
                    data-testid={"multi-window-bring-back-" + widget}
                  >
                    Bring back
                  </Button>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
